import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import dvdlocator from "../../../../apis/dvdlocator";
import { renderDate } from "../../../../formatting/dateFormat";
import { SUPPLIERS_BY_KEY } from "../suppliers";
import { classifyFreshness, Freshness } from "./SupplierRollupCell";
import {
  DriftCell,
  FreshnessDot,
  InventoryGridLink,
  ProvenanceCell,
  ProvenanceDialog,
  ProvenanceFrame,
  inventoryGridUrlForUpcs,
  useProvenanceDetail,
} from "./MetricProvenance";

/**
 * Restock status badge shared by the inventory, listing and product grids.
 *   IS  in stock somewhere          (green)
 *   BO  backorderable               (blue)
 *   NS  no tracked source           (grey)   all-suppliers status only
 *   OP  out of print                (red)
 *
 * The product-level cells (renderRestockStatusCell) open the detail: the
 * winning complete-set combination, the supply row that gave each part its
 * status then and now, and for the all-suppliers status the supplier
 * observation supporting it with a freshness dot (supplier data, unlike the
 * inventory metrics). The plain badge (renderRestockStatus) stays for the
 * supply-level columns.
 */
export const RESTOCK_STATUS_COLORS: Readonly<Record<string, string>> = {
  IS: "#339933",
  BO: "#0066FF",
  NS: "#888888",
  OP: "#CC0000",
};

export const RESTOCK_STATUS_LABELS: Readonly<Record<string, string>> = {
  IS: "In stock",
  BO: "Back order",
  NS: "No source",
  OP: "Out of print",
};

export type RestockKind = "FTP" | "ALL";

export const RESTOCK_KIND_LABELS: Record<RestockKind, string> = {
  FTP: "Restock (feeds): the Alliance and DealsAreUs feeds only",
  ALL: "Restock (all suppliers): every tracked supplier",
};

const STATUS_COLUMN: Record<RestockKind, string> = {
  FTP: "restock_status_calculated",
  ALL: "restock_status_all",
};

function Badge({ status }: { status: string }) {
  return (
    <span
      style={{
        color: RESTOCK_STATUS_COLORS[status],
        fontWeight: 700,
        fontSize: 17,
      }}
    >
      {status}
    </span>
  );
}

export function renderRestockStatus(
  status: string | null | undefined,
  updatedAt?: string | number | null
): React.ReactNode {
  if (!status || !RESTOCK_STATUS_COLORS[status]) return null;
  const title = [
    RESTOCK_STATUS_LABELS[status],
    updatedAt ? `Updated at: ${renderDate(updatedAt)}` : null,
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <span
      style={{
        color: RESTOCK_STATUS_COLORS[status],
        fontWeight: 700,
        fontSize: 17,
      }}
      title={title}
    >
      {status}
    </span>
  );
}

/** Freshness of the all-suppliers status: only IS and BO rest on an observation. */
export function restockFreshness(
  status: string | null | undefined,
  observedAt: string | null | undefined,
  undated: number | null | undefined,
  now?: Date
): Freshness | null {
  if (status !== "IS" && status !== "BO") return null;
  if (undated && undated > 0) return "never";
  return classifyFreshness(observedAt, now);
}

export function formatRestockTooltip(kind: RestockKind, row: any): string {
  const p = row?.product ?? {};
  const status = p[STATUS_COLUMN[kind]];
  const lines = [
    `${RESTOCK_KIND_LABELS[kind]}: ${status} (${
      RESTOCK_STATUS_LABELS[status] ?? "unknown"
    })`,
  ];
  if (kind === "ALL" && (status === "IS" || status === "BO")) {
    lines.push(
      p.restock_all_undated_parts > 0
        ? `${p.restock_all_undated_parts} part${
            p.restock_all_undated_parts === 1 ? "" : "s"
          } rest on an undated supplier observation`
        : p.restock_all_observed_at
        ? `Oldest supporting supplier observation ${renderDate(
            p.restock_all_observed_at
          )}`
        : "Supporting supplier observation undated"
    );
  }
  lines.push(
    `Calculated ${
      p.restock_updated_at ? renderDate(p.restock_updated_at) : "never"
    }.`,
    "Click for the parts behind it."
  );
  return lines.join("\n");
}

export const renderRestockStatusCell =
  (kind: RestockKind) => (params: GridRenderCellParams) => {
    const p = params.row?.product ?? {};
    const status = p[STATUS_COLUMN[kind]];
    if (!status || !RESTOCK_STATUS_COLORS[status]) return null;
    const freshness =
      kind === "ALL"
        ? restockFreshness(
            status,
            p.restock_all_observed_at,
            p.restock_all_undated_parts
          )
        : null;
    return (
      <ProvenanceCell
        tooltip={formatRestockTooltip(kind, params.row)}
        renderDetail={(close) => (
          <RestockDetail
            productId={p.product_id}
            productTitle={p.product_title}
            highlight={kind}
            onClose={close}
          />
        )}
      >
        <span>
          {freshness && <FreshnessDot freshness={freshness} />}
          <Badge status={status} />
        </span>
      </ProvenanceCell>
    );
  };

// ─── Detail ──────────────────────────────────────────────────────────────────

export interface RestockPart {
  part_number: string;
  inventory_id: number | null;
  inventory_title: string | null;
  upc: string | null;
  status_then: string | null;
  status_now: string | null;
  supplier_key: string | null;
  observed_at: string | null;
  current_observed_at: string | null;
}

export interface RestockKindDetail {
  status: string | null;
  combination_id: number | null;
  observed_at: string | null;
  undated_parts: number | null;
  parts: RestockPart[];
}

export interface RestockDetailData {
  product_id: number;
  updated_at: string | null;
  FTP: RestockKindDetail;
  ALL: RestockKindDetail;
}

export async function fetchRestockDetail(
  productId: number
): Promise<RestockDetailData> {
  const response = await dvdlocator.get(
    `/product/${productId}/metrics/restock`
  );
  return response.data;
}

function KindSection({
  kind,
  data,
  updatedAt,
  highlight,
}: {
  kind: RestockKind;
  data: RestockKindDetail;
  updatedAt: string | null;
  highlight?: RestockKind;
}) {
  const upcs = data.parts.map((p) => p.upc).filter((u): u is string => !!u);
  const freshness =
    kind === "ALL"
      ? restockFreshness(data.status, data.observed_at, data.undated_parts)
      : null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontWeight: kind === highlight ? 700 : 500,
        }}
      >
        {freshness && <FreshnessDot freshness={freshness} />}
        {RESTOCK_KIND_LABELS[kind]}:{" "}
        {data.status ? (
          <>
            <Badge status={data.status} />
            <span>({RESTOCK_STATUS_LABELS[data.status] ?? "unknown"})</span>
          </>
        ) : (
          "none"
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {data.status === null
          ? "No supply row of this product carries a status."
          : `Best combination #${
              data.combination_id ?? "?"
            } (its least available part decides), calculated ${
              updatedAt ? renderDate(updatedAt) : "never"
            }${
              kind === "ALL" && (data.status === "IS" || data.status === "BO")
                ? `; oldest supporting observation ${
                    data.observed_at ? renderDate(data.observed_at) : "never"
                  }${
                    data.undated_parts
                      ? `; ${data.undated_parts} part(s) undated`
                      : ""
                  }`
                : ""
            }. "Now" shows the row's current status when it changed since.`}
      </Typography>
      {data.parts.length > 0 && (
        <Table size="small" sx={{ mt: 0.5 }}>
          <TableHead>
            <TableRow>
              <TableCell>Part</TableCell>
              <TableCell>UPC</TableCell>
              <TableCell>Status then</TableCell>
              <TableCell>Now</TableCell>
              {kind === "ALL" && (
                <>
                  <TableCell>Supporting supplier</TableCell>
                  <TableCell>Observed</TableCell>
                  <TableCell>Observed now</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.parts.map((part) => {
              const supplier = part.supplier_key
                ? SUPPLIERS_BY_KEY[part.supplier_key]
                : undefined;
              return (
                <TableRow key={`${kind}-${part.part_number}`}>
                  <TableCell title={part.inventory_title ?? undefined}>
                    {part.part_number}
                  </TableCell>
                  <TableCell>{part.upc ?? "—"}</TableCell>
                  <TableCell>
                    {part.status_then ? (
                      <Badge status={part.status_then} />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <DriftCell
                      then={part.status_then}
                      now={part.status_now}
                      format={(v) => String(v)}
                      align="left"
                    />
                  </TableCell>
                  {kind === "ALL" && (
                    <>
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {supplier && (
                            <img
                              src={supplier.icon}
                              alt=""
                              width={14}
                              height={14}
                            />
                          )}
                          {supplier?.name ?? part.supplier_key ?? "—"}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {part.supplier_key && (
                          <FreshnessDot
                            freshness={classifyFreshness(part.observed_at)}
                          />
                        )}
                        {part.supplier_key
                          ? part.observed_at
                            ? renderDate(part.observed_at)
                            : "undated"
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {part.supplier_key ? (
                          <DriftCell
                            then={part.observed_at}
                            now={part.current_observed_at}
                            format={(v) => renderDate(v)}
                            align="left"
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <InventoryGridLink
        href={inventoryGridUrlForUpcs(Array.from(new Set(upcs)))}
        count={new Set(upcs).size}
      />
    </Box>
  );
}

export function RestockDetail({
  productId,
  productTitle,
  highlight,
  onClose,
}: {
  productId: number;
  productTitle?: string;
  highlight?: RestockKind;
  onClose?: () => void;
}) {
  const { detail, error } = useProvenanceDetail(
    productId,
    fetchRestockDetail,
    "restock status detail"
  );
  return (
    <ProvenanceFrame
      title="Restock status"
      productTitle={productTitle}
      productId={productId}
      error={error}
      loaded={detail !== null}
      recalcLabel="Recalculate restock status"
      onClose={onClose}
    >
      {detail && (
        <>
          <KindSection
            kind="ALL"
            data={detail.ALL}
            updatedAt={detail.updated_at}
            highlight={highlight}
          />
          <KindSection
            kind="FTP"
            data={detail.FTP}
            updatedAt={detail.updated_at}
            highlight={highlight}
          />
        </>
      )}
    </ProvenanceFrame>
  );
}

export function RestockDetailDialog({
  open,
  onClose,
  product,
}: {
  open: boolean;
  onClose: () => void;
  product: any;
}) {
  return (
    <ProvenanceDialog open={open} onClose={onClose} title="Restock status">
      <RestockDetail
        productId={product?.product_id}
        productTitle={product?.product_title}
      />
    </ProvenanceDialog>
  );
}
